import React, { useCallback, useState } from 'react';
import { useTranslations } from 'use-intl';
import Image from '@/components/atoms/Image';
import useFormat from '@/hooks/useFormat';
import Button from '@/components/atoms/button';
import InfoBanner from '@/components/molecules/info-banner';
import { classnames } from '@/utils/classnames/classnames';
import { OrderDetailsPageProps } from './types';
import OrderStatusBar from './components/order-status-bar';
import PreviousPageLink from '../../components/previous-page-link';

const OrderDetailsPage = ({
  order,
  viewOnly,
  onReturn,
  onReorder,
  showCtaButtons = true,
  showOrderStatusBar = true,
}: OrderDetailsPageProps) => {
  const translate = useTranslations();

  const { formatCurrency, formatLocalDate } = useFormat();

  const [loading, setLoading] = useState({ return: false, reorder: false });

  const handleReturn = useCallback(async () => {
    setLoading({ ...loading, return: true });

    await onReturn?.();

    setLoading({ ...loading, return: false });
  }, [loading, onReturn]);

  const handleReorder = useCallback(async () => {
    setLoading({ ...loading, reorder: true });

    await onReorder?.();

    setLoading({ ...loading, reorder: false });
  }, [loading, onReorder]);

  if (!order) return <></>;

  return (
    <div>
      {viewOnly && (
        <InfoBanner className="mt-3">
          <b>{translate('common.view-only')}</b> {translate('dashboard.orders-view-only-desc')}
        </InfoBanner>
      )}
      <div className="flex items-center justify-between">
        <h1 className="text-18 md:text-20 lg:text-24 py-6 font-extrabold text-gray-800 md:py-7 lg:py-9">
          {translate('dashboard.order-details')}
        </h1>
        {showCtaButtons && (
          <div className="hidden items-center justify-normal gap-x-3 md:flex">
            <PreviousPageLink />

            {!viewOnly && order.status === 'Delivered' && (
              <Button size="s" variant="secondary" onClick={handleReturn} loading={loading.return}>
                {translate('orders.return')}
              </Button>
            )}
            {!viewOnly && !order.isFromAQuote && (
              <Button size="s" variant="primary" onClick={handleReorder} loading={loading.reorder}>
                {translate('orders.reorder')}
              </Button>
            )}
          </div>
        )}
      </div>

      <h3 className="text-14 text-gray-600">
        {translate('dashboard.order-id')}: {order.number}
      </h3>

      {order.creationDate && (
        <h3 className="text-14 mt-4 text-gray-600">
          {translate('dashboard.order-date')}: {formatLocalDate(order.creationDate)}
        </h3>
      )}

      {order.purchaseOrderNumber && (
        <h3 className="text-14 mt-4 text-gray-600">
          {translate('dashboard.purchase-order-number')}: {order.purchaseOrderNumber}
        </h3>
      )}

      {order.invoiceNumber && (
        <h3 className="text-14 mt-4 text-gray-600">
          {translate('common.invoice-number')}: {order.invoiceNumber}
        </h3>
      )}

      {order.channel && (
        <h3 className="text-14 mt-4 text-gray-600">
          {translate('common.channel')}: {order.channel}
        </h3>
      )}

      {showOrderStatusBar && (
        <div className="pt-12 pb-8 md:border-b md:border-neutral-400 md:pt-[56px] md:pb-12 lg:pb-[56px]">
          <OrderStatusBar order={order} />
        </div>
      )}

      {showCtaButtons && (
        <div className="flex flex-col items-center justify-normal gap-y-4 md:hidden">
          <PreviousPageLink />

          {!viewOnly && order.status === 'Delivered' && (
            <Button size="full" variant="secondary" onClick={handleReturn} loading={loading.return}>
              {translate('orders.return')}
            </Button>
          )}

          {!viewOnly && !order.isFromAQuote && (
            <Button size="full" variant="primary" onClick={handleReorder} loading={loading.reorder}>
              {translate('orders.reorder')}
            </Button>
          )}
        </div>
      )}

      <div className={classnames({ 'mt-8 border-t border-neutral-400 md:mt-12': !showCtaButtons })}>
        <h5 className="pt-6 pb-7 text-gray-700">
          {translate('dashboard.items-ordered')} <span className="text-gray-600">({order.items.length})</span>
        </h5>

        <table className="w-full border-collapse">
          <thead>
            <tr className="text-12 border-b border-neutral-400 p-4 font-semibold text-gray-500 uppercase">
              <th className="p-4 text-left">{translate('common.product')}</th>
              <th className="hidden p-4 text-left md:table-cell">{translate('common.cat-no')}</th>
              <th className="hidden p-4 text-left md:table-cell">{translate('common.variant')}</th>
              <th className="hidden p-4 text-right md:table-cell">{translate('common.qty')}</th>
              <th className="hidden p-4 text-right lg:table-cell">
                {translate('common.price')} {''}
                <span className="text-gray-400 normal-case">({translate('common.ex-gst')})</span>
              </th>
              <th className="hidden p-4 text-right lg:table-cell">
                {translate('common.total')} {''}
                <span className="text-gray-400 normal-case">({translate('common.ex-gst')})</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {order.items.map(({ id, images, name, sku, variant, quantity, price, currency }) => (
              <tr key={id} className="text-14 border-b border-neutral-400 p-4 text-gray-600">
                <td className="p-4 text-left">
                  <div className="flex items-center gap-3">
                    <span className="relative block size-[80px] shrink-0">
                      <Image src={images?.[0]} fill alt={name ?? ''} />
                    </span>
                    <span>{name}</span>
                  </div>
                </td>
                <td className="hidden p-4 text-left md:table-cell">{sku}</td>
                <td className="hidden p-4 text-left md:table-cell">{variant || '—'}</td>
                <td className="hidden p-4 text-right md:table-cell">{quantity}</td>
                <td className="hidden p-4 text-right lg:table-cell">{formatCurrency(price ?? 0, currency ?? 'USD')}</td>
                <td className="hidden p-4 text-right lg:table-cell">
                  {formatCurrency((price ?? 0) * (quantity ?? 1), currency ?? 'USD')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end pt-6 pb-7">
        <div className="flex w-full flex-col gap-2 lg:w-1/2">
          <div className="text-14 flex items-center justify-between text-gray-600">
            <span>{translate('common.subtotal')}</span>
            <span>{formatCurrency(order.subtotal, order.currency)}</span>
          </div>

          {!!order.volumeDiscount && (
            <div className="text-14 flex items-center justify-between text-gray-600">
              <span>{translate('common.volume-discount')}</span>
              <span>-{formatCurrency(order.volumeDiscount, order.currency)}</span>
            </div>
          )}

          {!!order.shippingCosts && (
            <div className="text-14 flex items-center justify-between text-gray-600">
              <span>{translate('common.freight')}</span>
              <span>{formatCurrency(order.shippingCosts, order.currency)}</span>
            </div>
          )}

          {!!order.taxCosts && (
            <div className="text-14 flex items-center justify-between text-gray-600">
              <span>{translate('common.gst')}</span>
              <span>{formatCurrency(order.taxCosts, order.currency)}</span>
            </div>
          )}

          <div className="flex items-center justify-between border-t border-neutral-400 pt-2 font-medium text-gray-700">
            <span>{translate('common.total')}:</span>
            <span>{formatCurrency(order.total, order.currency)}</span>
          </div>
        </div>
      </div>

      <div>
        <h5 className="border-t border-neutral-400 pt-6 pb-7 text-gray-700">
          {translate('dashboard.shipping-and-payment')}
        </h5>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-3 lg:grid-cols-4">
          <div>
            <h6 className="text-14 text-gray-500 uppercase">{translate('common.address-shipping')}</h6>
            <p className="text-14 mt-2 whitespace-pre-line text-gray-700">{order.shippingAddress}</p>
          </div>

          {order.billingAddress && order.billingAddress !== order.shippingAddress && (
            <div>
              <h6 className="text-14 text-gray-500 uppercase">{translate('common.address-billing')}</h6>
              <p className="text-14 mt-2 whitespace-pre-line text-gray-700">{order.billingAddress}</p>
            </div>
          )}

          <div>
            <h6 className="text-14 text-gray-500 uppercase">{translate('common.shipping-method')}</h6>
            <p className="text-14 mt-2 whitespace-pre-line text-gray-700">{order.shippingMethod}</p>
          </div>

          <div>
            <h6 className="text-14 text-gray-500 uppercase">{translate('common.payment-method')}</h6>
            <p className="text-14 mt-2 whitespace-pre-line text-gray-700">{order.paymentMethod}</p>
          </div>
        </div>
      </div>

      {(order.estimatedDelivery || order.trackingNumber) && (
        <div className="mt-6 border-t border-neutral-400">
          <h5 className="pt-6 pb-7 text-gray-700">{translate('common.delivery-info')}</h5>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {order.estimatedDelivery && (
              <div>
                <h6 className="text-14 text-gray-500 uppercase">{translate('common.estimated-delivery')}</h6>
                <p className="text-14 mt-2 text-gray-700">{order.estimatedDelivery}</p>
              </div>
            )}
            {order.trackingNumber && (
              <div>
                <h6 className="text-14 text-gray-500 uppercase">{translate('common.tracking')}</h6>
                <p className="text-14 mt-2 text-gray-700">
                  {order.trackingUrl ? (
                    <a
                      href={order.trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline hover:opacity-80"
                    >
                      {order.trackingNumber}
                    </a>
                  ) : (
                    order.trackingNumber
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {(order.placedBy || order.placedFor) && (
        <div className="mt-6 border-t border-neutral-400">
          <h5 className="pt-6 pb-4 text-gray-700">{translate('dashboard.order-on-behalf')}</h5>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {order.placedBy && (
              <div>
                <h6 className="text-14 text-gray-500 uppercase">{translate('common.placed-by')}</h6>
                <p className="text-14 mt-2 text-gray-700">{order.placedBy}</p>
              </div>
            )}
            {order.placedFor && (
              <div>
                <h6 className="text-14 text-gray-500 uppercase">{translate('common.placed-for')}</h6>
                <p className="text-14 mt-2 text-gray-700">{order.placedFor}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetailsPage;
